import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Plot from 'react-plotly.js';
import FilterBar from '../components/FilterBar';
import Panel from '../components/Panel';
import WaveLoader from '../components/WaveLoader';

import { typeLabel } from '../utils/entityLabels';

import WeekNote from '../components/WeekNote';

// ---------------------------------------------------------------------------
// Shared micro-components (InsightCard, KpiCard)
// ---------------------------------------------------------------------------

function InsightCard({ label, title, body }) {
  return (
    <div style={{
      background: 'var(--card-bg)',
      border: '1px solid var(--card-border)',
      borderRadius: '24px',
      padding: '24px',
      boxShadow: 'var(--shadow-md)',
      height: '100%',
      boxSizing: 'border-box',
    }}>
      <div style={{ fontSize: '11px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.22em', color: '#8b7355', marginBottom: '12px' }}>
        {label}
      </div>
      <div style={{ fontFamily: "'Inter', sans-serif", fontSize: '32px', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text-dark)', lineHeight: 1.3, marginBottom: '12px' }}>
        {title}
      </div>
      <p style={{ fontSize: '14px', lineHeight: 1.75, color: 'var(--text-light)', margin: 0 }}>{body}</p>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Home page
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Time-range defaults
// ---------------------------------------------------------------------------
// Two default scopes govern the Home page when the user has NOT set a filter:
//
//   OVERVIEW_DEFAULT (365 days) — used for summary KPIs and the Signal Flow
//       chart that show the full historical picture.
//
//   RECENT_DEFAULT (7 days) — used for the "Leading Signal" card and the
//       "Top Semantic Signals" chart that highlight what is happening *now*.
//
// When the user explicitly chooses a range — either an absolute date_from /
// date_to from the filter bar, or a time_range other than the overview default
// — that range is used for ALL widgets, both overview and recent, so the
// entire page reflects the user's intent.
//
// The frontend passes `recent_range` (in days) to the backend API alongside
// the regular filters. The backend uses it as-is for its recent-widget queries;
// it has no knowledge of the two-tier default logic.
// ---------------------------------------------------------------------------
const OVERVIEW_DEFAULT = '365';
const RECENT_DEFAULT = '30';

function timeRangeLabel(days) {
  const n = Number(days);
  if (n === 7) return 'the last week';
  if (n === 14) return 'the last 2 weeks';
  if (n === 30) return 'the last month';
  if (n === 90) return 'the last 3 months';
  if (n === 180) return 'the last 6 months';
  if (n === 365) return 'the last year';
  return `the last ${days} days`;
}

// Absolute dates from the filter bar override the rolling window, so the copy on the
// recent widgets has to name the range the user actually picked.
function scopeLabel(filters, recentDays) {
  const from = filters.date_from;
  const to = filters.date_to;
  if (from && to) return `the selected range (${from} – ${to})`;
  if (from) return `the range since ${from}`;
  if (to) return `the range up to ${to}`;
  return timeRangeLabel(recentDays);
}

function Home({ filters, loading, setLoading, isStaging, onDrilldown }) {
  const [summary, setSummary] = useState({});
  const [trends, setTrends] = useState([]);
  const [topSignals, setTopSignals] = useState([]);
  const [signalTrends, setSignalTrends] = useState([]);
  const [signalBasis, setSignalBasis] = useState('weekly');
  // '' means "All types" — the chart then ranks entities across every type, which is the
  // page's default view. Picking a type scopes the top-N to that type alone.
  const [signalType, setSignalType] = useState('');
  const [signalTypeOptions, setSignalTypeOptions] = useState([]);
  // Two independent fetches. The summary drives the whole page and owns the page-level
  // `loading` flag; the signal-trends chart has its own entity-type control, so changing
  // that type must refresh only the chart — not blank out the KPIs and Signal Flow.
  const summaryFetchIdRef = React.useRef(0);
  const signalFetchIdRef = React.useRef(0);
  // The fetch-id refs only discard stale responses; the server still completes every
  // superseded request. Home also auto-refreshes every 2 min, so without aborting, a
  // filter edit could leave several full page loads competing for the same gunicorn
  // workers.
  const summaryAbortRef = React.useRef(null);
  const signalAbortRef = React.useRef(null);
  const [signalLoading, setSignalLoading] = useState(false);

  const hasAbsoluteRange = !!(filters.date_from || filters.date_to);
  const userSetRange = filters.time_range && filters.time_range !== '' && filters.time_range !== OVERVIEW_DEFAULT;
  // With absolute dates the backend scopes the recent widgets by those dates and ignores
  // recent_range, so the rolling default is what we keep sending.
  const recentRange = !hasAbsoluteRange && userSetRange ? filters.time_range : RECENT_DEFAULT;
  const rangeLabel = scopeLabel(filters, recentRange);

  const buildParams = useCallback(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== '') params.append(k, v);
    });
    params.set('recent_range', recentRange);
    return params;
  }, [filters, recentRange]);

  const fetchSummary = useCallback(() => {
    const id = ++summaryFetchIdRef.current;
    if (summaryAbortRef.current) summaryAbortRef.current.abort();
    const ctrl = new AbortController();
    summaryAbortRef.current = ctrl;
    setLoading(true);
    const summaryParams = buildParams();
    // topSignals is one row per entity type, and the Leading Signal card renders every
    // row it gets — so this limit is the number of type leaders shown, not a top-N over
    // a flat ranking.
    summaryParams.set('limit', '6');
    axios.get(`/api/home/summary?${summaryParams}`, { signal: ctrl.signal })
      .then(res => {
        if (id !== summaryFetchIdRef.current) return;
        setSummary(res.data.summary || {});
        setTrends(res.data.trends || []);
        setTopSignals(res.data.topSignals || []);
      })
      .catch(err => { if (id === summaryFetchIdRef.current && !axios.isCancel(err)) console.error('Error loading Home summary:', err); })
      .finally(() => { if (id === summaryFetchIdRef.current) setLoading(false); });
  }, [buildParams, setLoading]);

  const fetchSignalTrends = useCallback(() => {
    const id = ++signalFetchIdRef.current;
    if (signalAbortRef.current) signalAbortRef.current.abort();
    const ctrl = new AbortController();
    signalAbortRef.current = ctrl;
    setSignalLoading(true);
    const trendParams = buildParams();
    trendParams.set('limit', '5');
    if (signalType) trendParams.set('entity_type', signalType);
    axios.get(`/api/home/signal-trends?${trendParams}`, { signal: ctrl.signal })
      .then(res => {
        if (id !== signalFetchIdRef.current) return;
        setSignalTrends(res.data.signal_trends || []);
        setSignalTypeOptions(res.data.available_types || []);
        // The backend picks daily vs weekly from the window actually being charted, so
        // the axis and subtitle have to follow it rather than assume one or the other.
        setSignalBasis(res.data.basis || 'weekly');
      })
      .catch(err => { if (id === signalFetchIdRef.current && !axios.isCancel(err)) console.error('Error loading Home signal trends:', err); })
      .finally(() => { if (id === signalFetchIdRef.current) setSignalLoading(false); });
  }, [buildParams, signalType]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  useEffect(() => { fetchSignalTrends(); }, [fetchSignalTrends]);

  // Auto-refresh every 2 min
  useEffect(() => {
    const interval = setInterval(() => { fetchSummary(); fetchSignalTrends(); }, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchSummary, fetchSignalTrends]);

  // Derived insight values. topSignals arrives as one leader per entity type, already
  // ordered strongest-first, so [0] headlines the card and the rest become the sentence
  // that follows. Types with no mentions in the window are simply absent from the
  // response, so nothing has to be filtered out here.
  const topSignal = topSignals[0];
  const otherSignals = topSignals.slice(1);

  // Built as nodes, not a string, so the entity and type names can be bold. InsightCard
  // renders `body` inside a <p>, which takes inline elements fine.
  let leadingSignalBody;
  if (!topSignal) {
    leadingSignalBody = 'Signal density is too thin in the current scope to identify a clear leader.';
  } else {
    const plural = topSignal.mention_count === 1 ? '' : 's';
    leadingSignalBody = (
      <>
        <strong>{topSignal.signal_name}</strong>{` was mentioned in ${topSignal.mention_count} conversation${plural} across ${rangeLabel}.`}
        {otherSignals.length > 0 && (
          <>
            {' By type, '}
            {otherSignals.map((sig, i) => (
              <React.Fragment key={`${sig.dashboard_dimension}|${sig.signal_name}`}>
                {i === 0 ? '' : i === otherSignals.length - 1 ? ' and ' : ', '}
                {'the most-mentioned '}
                <strong>{typeLabel(sig.dashboard_dimension)}</strong>
                {' is '}
                <strong>{sig.signal_name}</strong>
                {` (${sig.mention_count})`}
              </React.Fragment>
            ))}
            .
          </>
        )}
      </>
    );
  }

  // Minutes are shown as a plain minute count because that is the unit asked for; the
  // hour figure rides along in the tooltip for anyone who wants the intuitive scale.
  const fmtInt = n => Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 });
  const fmt1 = n => Number(n).toLocaleString(undefined, { maximumFractionDigits: 1 });

  // Both figures come from the rows the chart itself draws, so the two can never
  // disagree. Talk time deliberately does NOT reuse summary.total_minutes: that query has
  // no `call_date IS NOT NULL`, so on an org with an undated conversation it would state a
  // total measured over different rows than the conversation count beside it.
  // Number() on both, not just arithmetic: a numeric column can reach the client as a
  // JSON string (Postgres numeric -> Decimal -> Flask), and `0 + "168.4"` concatenates
  // rather than adds, which showed up on screen as "NaN min talk time".
  const trendTotals = trends.reduce((a, t) => ({
    conversations: a.conversations + (Number(t.conversations) || 0),
    minutes: a.minutes + (Number(t.total_minutes) || 0),
  }), { conversations: 0, minutes: 0 });
  const hasTrendMinutes = trends.some(t => t.total_minutes != null);

  // Plotly trend chart
  const trendData = trends.length ? [
    {
      x: trends.map(t => t.week_start),
      y: trends.map(t => t.conversations || 0),
      name: 'Conversations',
      type: 'scatter',
      mode: 'lines+markers',
      line: { color: '#1f5eff', width: 3 },
    },
  ] : [];

  // Signals time-series chart
  const signalColors = ['#1f5eff', '#1f7a3f', '#c98b12', '#c2410c', '#7c3aed', '#0f766e', '#1a4fd6', '#e11d48', '#6366f1', '#059669'];
  const signalTrendData = (() => {
    if (!signalTrends.length) return [];
    const byName = {};
    signalTrends.forEach(r => {
      if (!byName[r.signal_name]) byName[r.signal_name] = [];
      byName[r.signal_name].push(r);
    });
    return Object.entries(byName).map(([name, rows], i) => ({
      x: rows.map(r => r.period),
      y: rows.map(r => r.mention_count),
      name,
      type: 'scatter',
      mode: 'lines+markers',
      line: { color: signalColors[i % signalColors.length], width: 2 },
      marker: { size: 4 },
    }));
  })();

  return (
    <div>
      {/* Header */}
      <div className="module-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.22em', color: 'var(--text-lighter)', marginBottom: '6px' }}>
              Command Center
            </div>
            <h2 style={{ marginBottom: '4px' }}>Overview</h2>
            <p>Live executive view across volume, sentiment, quality, and the strongest semantic signals.</p>
          </div>
          <button
            type="button"
            onClick={() => { fetchSummary(); fetchSignalTrends(); }}
            disabled={loading}
            style={{
              padding: '10px 18px', fontSize: '14px', fontWeight: 600, color: '#fff',
              background: '#1f5eff', border: 'none', borderRadius: '12px',
              cursor: loading ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px',
            }}
          >
            <span aria-hidden>↻</span> {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      <FilterBar />

      {/* KPI Cards + Leading Signal */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '32px', alignItems: 'stretch' }}>
        <div style={{ display: 'flex', gap: '16px' }}>
          <div style={{ flex: 1, display: 'flex' }}>
            <div style={{ flex: 1 }} className="metric-card">
              <span className="metric-label">Total Conversations</span>
              <div className="metric-value" style={{ color: '#1f7a3f' }}>{summary.conversation_count ?? 0}</div>
              <div style={{ fontSize: '12px', color: 'var(--text-lighter)', marginTop: '6px' }}>
                Normalized transcript records
                {/* A separate line, not appended above, so an org whose pipeline records
                    no call length keeps the original card rather than showing "0 min"
                    as though every call were instantaneous. */}
                {summary.total_minutes != null && (
                  <div
                    style={{ marginTop: '3px' }}
                    title={`${fmt1(summary.total_minutes / 60)} hours of recorded conversation`}
                  >
                    {fmtInt(summary.total_minutes)} min total
                    {summary.avg_minutes != null && ` · ${fmt1(summary.avg_minutes)} min avg`}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div style={{ flex: 1, display: 'flex' }}>
            <div style={{ flex: 1 }} className="metric-card">
              <span className="metric-label">Active Agents</span>
              <div className="metric-value" style={{ color: '#c98b12' }}>{summary.agent_count ?? 0}</div>
              <div style={{ fontSize: '12px', color: 'var(--text-lighter)', marginTop: '6px' }}>Mapped transcript ownership</div>
            </div>
          </div>
        </div>
        <InsightCard
          label="Leading Signal"
          title={topSignal
            ? `${topSignal.signal_name} is the most-mentioned ${topSignal.dashboard_dimension}.`
            : 'No dominant signal yet.'}
          body={leadingSignalBody}
        />
      </div>

      {/* Charts Row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '32px' }}>
        <Panel
          title="Signal Flow"
          subtitle="Weekly conversation activity"
          loading={loading}
        >
          {trends.length ? (
            <>
              <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', marginBottom: '10px', fontSize: '12px', color: 'var(--text-light)' }}>
                <span><strong style={{ color: 'var(--text-dark)' }}>{fmtInt(trendTotals.conversations)}</strong> conversations</span>
                {hasTrendMinutes && (
                  <span><strong style={{ color: 'var(--text-dark)' }}>{fmtInt(trendTotals.minutes)}</strong> min talk time</span>
                )}
                <span><strong style={{ color: 'var(--text-dark)' }}>{trends.length}</strong> {trends.length === 1 ? 'week' : 'weeks'}</span>
              </div>
              <Plot
              data={trendData}
              layout={{
                xaxis: { title: 'Week starting' },
                yaxis: { title: 'Conversations' },
                hovermode: 'x unified',
                plot_bgcolor: 'var(--bg-dark)',
                paper_bgcolor: 'var(--card-bg)',
                margin: { t: 20, r: 20, b: 50, l: 60 },
                height: 320,
                showlegend: false,
              }}
              config={{ responsive: true, displaylogo: false, modeBarButtonsToRemove: ['pan2d', 'select2d', 'lasso2d', 'autoScale2d'] }}
              style={{ width: '100%' }}
            />
              <WeekNote />
            </>
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-lighter)' }}>No data available</div>
          )}
        </Panel>

        <Panel
          title="Top Entity Mentions"
          subtitle={`Up to 5 most-mentioned ${signalType ? `${typeLabel(signalType)} entities` : 'entities'} over ${rangeLabel}, per ${signalBasis === 'daily' ? 'day' : 'week'}`}
          loading={false}
        >
          <div style={{ marginBottom: '16px' }}>
            <label
              htmlFor="home-signal-type"
              style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-light)', display: 'block', marginBottom: '6px' }}
            >
              Entity Type
            </label>
            <select
              id="home-signal-type"
              value={signalType}
              onChange={e => setSignalType(e.target.value)}
              style={{
                padding: '8px 12px', border: '1px solid var(--card-border)', borderRadius: '10px',
                background: 'var(--card-bg)', color: 'var(--text-dark)', fontSize: '14px',
              }}
            >
              <option value="">All types</option>
              {/* A chosen type can drop out of the list when the date filter changes and
                  that type has no mentions in the new window. Keep it as an option so the
                  select still shows what is selected instead of rendering blank. */}
              {(signalTypeOptions.includes(signalType) || !signalType
                ? signalTypeOptions
                : [signalType, ...signalTypeOptions]
              ).map(t => <option key={t} value={t}>{typeLabel(t)}</option>)}
            </select>
          </div>
          {signalLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '150px 0' }}><WaveLoader /></div>
          ) : signalTrendData.length ? (
            <>
              <Plot
              data={signalTrendData}
              layout={{
                xaxis: { title: signalBasis === 'daily' ? 'Day' : 'Week starting' },
                yaxis: { title: 'Mentions' },
                hovermode: 'x unified',
                plot_bgcolor: 'var(--bg-dark)',
                paper_bgcolor: 'var(--card-bg)',
                margin: { t: 20, r: 20, b: 120, l: 60 },
                height: 440,
                legend: { orientation: 'h', y: -0.25, x: 0, xanchor: 'left', font: { size: 11 }, traceorder: 'normal' },
              }}
              config={{ responsive: true, displaylogo: false, modeBarButtonsToRemove: ['pan2d', 'select2d', 'lasso2d', 'autoScale2d'] }}
              style={{ width: '100%' }}
            />
              {/* Follows the same signal the axis title does — WeekNote renders nothing
                  when the backend switched this chart to a daily bucket. */}
              <WeekNote basis={signalBasis} />
            </>
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-lighter)' }}>
              {signalType
                ? `No ${typeLabel(signalType)} mentions in ${rangeLabel}.`
                : 'No data available'}
            </div>
          )}
        </Panel>
      </div>

    </div>
  );
}

export default Home;
